import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  HealthAndSafety as ReliabilityIcon,
  Dashboard as DashboardIcon,
  Engineering as EngineeringIcon,
  Inventory as AssetsIcon,
  ManageAccounts as ServiceManagementIcon,
  RocketLaunch as DeliveryIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import ReliabilityProgressDashboard from './ReliabilityProgressDashboard';
import ReliabilityProductEngineering from './ReliabilityProductEngineering';
import ReliabilityAssets from './ReliabilityAssets';
import ServiceManagement from './ServiceManagement';
import ReliabilityDeliveryView from './ReliabilityDeliveryView';

const PRIMARY = '#0f1f3d';
const NAVY2  = '#1a3460';
const MUTED  = '#6d788c';
const SURFACE = '#f6f8fb';
const TAB_MAP = { dashboard: 0, 'product-engineering': 1, 'service-management': 2, assets: 3, delivery: 4 };

const NAV_ITEMS = [
  { icon: DashboardIcon, label: 'Dashboard' },
  { icon: EngineeringIcon, label: 'Product Engineering' },
  { icon: ServiceManagementIcon, label: 'Service Management' },
  { icon: AssetsIcon, label: 'Assets' },
  { icon: DeliveryIcon, label: 'Delivery' },
];

const ReliabilityDashboard = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const initialFilter = searchParams.get('filter');

  const [tabValue, setTabValue] = useState(
    initialTab && TAB_MAP[initialTab] !== undefined ? TAB_MAP[initialTab] : 0
  );
  const [pillarFilter, setPillarFilter] = useState(initialFilter || '');

  useEffect(() => {
    if (initialTab && TAB_MAP[initialTab] !== undefined) setTabValue(TAB_MAP[initialTab]);
  }, [initialTab]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', bgcolor: '#edf1f7', minHeight: '100%', borderRadius: 2, p: 0 }}>
      <Box sx={{
        background: `linear-gradient(105deg, ${PRIMARY} 0%, ${NAVY2} 100%)`,
        borderRadius: '12px 12px 0 0',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ReliabilityIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 720, fontSize: 20, letterSpacing: '-0.3px', color: '#fff', lineHeight: 1.2 }}>
            Reliability &amp; Scale Program
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#cfe0eb', mt: 0.3 }}>
            Platform resilience tracking across three engineering pillars
          </Typography>
        </Box>
      </Box>

      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe5ee', px: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.25, py: 0.5 }}>
          {NAV_ITEMS.map((item, idx) => {
            const Icon = item.icon;
            const active = tabValue === idx;
            return (
              <Box
                key={idx}
                onClick={() => setTabValue(idx)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.75, py: 0.875,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: active ? `${PRIMARY}12` : 'transparent',
                  color: active ? PRIMARY : MUTED,
                  borderBottom: active ? `2.5px solid ${PRIMARY}` : '2.5px solid transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: active ? `${PRIMARY}12` : '#f6f8fb', color: active ? PRIMARY : '#455168' },
                  userSelect: 'none',
                }}
              >
                <Icon sx={{ fontSize: 17, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: active ? 650 : 500, whiteSpace: 'nowrap' }}>
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {tabValue === 0 && (
          <ReliabilityProgressDashboard
            onPillarClick={(key) => { setPillarFilter(key); setTabValue(1); }}
            onSmClick={() => setTabValue(2)}
          />
        )}
        {tabValue === 1 && <ReliabilityProductEngineering initialFilter={pillarFilter} />}
        {tabValue === 2 && <ServiceManagement />}
        {tabValue === 3 && <ReliabilityAssets />}
        {tabValue === 4 && <ReliabilityDeliveryView />}
      </Box>
    </Box>
  );
};

export default ReliabilityDashboard;
